import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, ChevronLeft, ChevronRight, Clock3, MapPin, Menu, X } from "lucide-react";
import useHomeEvents, { HomeEvent } from "../hooks/useHomeEvents";
import HomeIcons from "../components/home/HomeIcons";
import HomeMarketing from "../components/home/HomeMarketing";
import HomeFooter from "../components/home/HomeFooter";
import "../styles/homepage.css";

const countries = [
  { key: "rwanda", flag: "🇷🇼", name: "Rwanda" },
  { key: "tanzania", flag: "🇹🇿", name: "Tanzania" },
  { key: "uganda", flag: "🇺🇬", name: "Uganda" },
  { key: "kenya", flag: "🇰🇪", name: "Kenya" },
  { key: "ethiopia", flag: "🇪🇹", name: "Ethiopia" },
];

function CountryFilter({ country, onChange }: { country: string; onChange: (country: string) => void }) {
  const selected = countries.find(item => item.key === country) || countries[0];
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();

  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return <div className="home-country-filter" ref={rootRef} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }} onKeyDown={event => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setActiveIndex(countries.indexOf(selected));
        setOpen(true);
      } else {
        setActiveIndex(index => (index + (event.key === "ArrowDown" ? 1 : -1) + countries.length) % countries.length);
      }
    } else if (open && (event.key === "Home" || event.key === "End")) {
      event.preventDefault();
      setActiveIndex(event.key === "Home" ? 0 : countries.length - 1);
    }
  }}>
    <button className="home-country-trigger" type="button" ref={triggerRef} aria-label={`Change country, ${selected.name}`} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => {
      setActiveIndex(countries.indexOf(selected));
      setOpen(!open);
    }}>
      <span className="home-country-flag" aria-hidden="true">{selected.flag}</span>
    </button>
    {open && <div className="home-country-menu">
      <p className="home-country-menu-title">Choose your country</p>
      <div id={menuId} role="listbox" aria-label="Country">
        {countries.map((item, index) => <button key={item.key} className="home-country-option" type="button" role="option" aria-selected={country === item.key} tabIndex={activeIndex === index ? 0 : -1} ref={element => { optionRefs.current[index] = element; }} onFocus={() => setActiveIndex(index)} onClick={() => {
          onChange(item.key);
          close();
        }}>
          <span className="home-country-flag" aria-hidden="true">{item.flag}</span><span>{item.name}</span>{country === item.key && <Check aria-hidden="true" />}
        </button>)}
      </div>
    </div>}
  </div>;
}
const navLinks = [["#discover", "Discover"], ["#social", "The app"], ["#organizers", "Organizers"]];
const date = (value: string, options: Intl.DateTimeFormatOptions) => new Date(value).toLocaleDateString("en", options);
const eventPath = (event: HomeEvent) => event.slug ? `/e/${event.slug}` : `/events/${event.id}`;
function price(event: HomeEvent) {
  if (!event.tickets?.length) return "See event details";
  const ticket = event.tickets.reduce((lowest, tier) => Number(tier.price) < Number(lowest.price) ? tier : lowest);
  return Number(ticket.price) === 0 ? "Free" : `${event.tickets.length > 1 ? "From " : ""}${ticket.currency} ${Number(ticket.price).toLocaleString()}`;
}

function HomeEventCard({ event, rail = false }: { event: HomeEvent; rail?: boolean }) {
  return <article className={rail ? "rail-card" : "card is-in"}>
    {rail ? <img src={event.image || "/images/logo/main-logo.png"} alt="" /> : <div className="card__media">
      <img src={event.image || "/images/logo/main-logo.png"} alt="" loading="lazy" />
      <span className="card__date"><b>{date(event.startDate, { day: "numeric" })}</b><small>{date(event.startDate, { month: "short" })}</small></span>
    </div>}
    {rail && <div className="rail-card__top"><span className="pill">{event.category?.name || "Event"}</span></div>}
    <div className={rail ? "rail-card__body" : "card__body"}>
      {!rail && <span className="card__cat">{event.category?.name || "Event"}</span>}
      <h3 className={rail ? "rail-card__title" : "card__title"}>{event.name}</h3>
      <div className={rail ? "rail-card__meta" : "card__meta"}>
        <span><Clock3 /><em>{date(event.startDate, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</em></span>
        <span><MapPin /><em>{event.location?.name || event.location?.address || "Location to be announced"}</em></span>
      </div>
      <div className={rail ? "rail-card__cta" : "card__foot"}>
        <span className={rail ? "rail-card__price" : "card__price"}>{price(event)}</span>
        <span className={rail ? "rail-card__go" : "card__go"}>{!rail && "Details"}{rail ? <ArrowUpRight /> : <ArrowRight />}</span>
      </div>
    </div>
    <Link className="card__link" to={eventPath(event)} aria-label={`View ${event.name}`} />
  </article>;
}

export default function Home() {
  const [country, setCountry] = useState("rwanda");
  const { events, loading, failed } = useHomeEvents(country, "next24Hours");
  const discovery = useHomeEvents(country, "after24Hours", {
    limit: 8,
    excludeEventIds: events.map(event => event.id),
    enabled: !loading,
  });
  const countryName = countries.find(item => item.key === country)?.name || "Rwanda";
  const [menuOpen, setMenuOpen] = useState(false);
  const [railEdges, setRailEdges] = useState({ canGoBack: false, canGoForward: false });
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const updateEdges = () => {
      const next = {
        canGoBack: rail.scrollLeft > 1,
        canGoForward: rail.scrollLeft < rail.scrollWidth - rail.clientWidth - 1,
      };
      setRailEdges(previous => previous.canGoBack === next.canGoBack && previous.canGoForward === next.canGoForward ? previous : next);
    };
    updateEdges();
    const observer = new ResizeObserver(updateEdges);
    observer.observe(rail);
    rail.addEventListener("scroll", updateEdges, { passive: true });
    return () => {
      observer.disconnect();
      rail.removeEventListener("scroll", updateEdges);
    };
  }, [events]);
  const scrollRail = (direction: number) => {
    const rail = railRef.current;
    const card = rail?.firstElementChild;
    if (!rail || !card) return;
    const distance = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(rail).columnGap) || 0);
    rail.scrollBy({ left: direction * distance, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  const posters = events.filter(event => event.image);
  const fallbackPosters = ["stage-crowd", "club-blue", "park-party", "cheers", "headphones", "crowd-selfie"];
  return <div className={`homepage${menuOpen ? " menu-open" : ""}`} id="top">
    <HomeIcons />
    <a className="home-skip" href="#main-content">Skip to content</a>
    <header className="nav is-scrolled">
      <div className="wrap nav__inner">
        <a href="#top" className="nav__logo" aria-label="CityMoshi home"><img src="/homepage/img/logo.png" alt="CityMoshi" width="150" height="38" /></a>
        <nav className="nav__links" aria-label="Main navigation">{navLinks.map(([href, label]) => <a key={href} href={href}>{label}</a>)}</nav>
        <div className="nav__actions">
          <CountryFilter country={country} onChange={setCountry} />
          <a className="btn btn--ghost btn--sm" href="https://dashboard.citymoshi.com" target="_blank" rel="noreferrer">Host an event</a>
          <a className="btn btn--primary btn--sm" href="#download">Get the app</a>
          <button className="nav__burger" type="button" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} aria-controls="home-mobile-menu" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </div>
    </header>
    {menuOpen && <nav id="home-mobile-menu" className="home-mobile-menu" aria-label="Mobile navigation">{[...navLinks, ["#download", "Get the app"]].map(([href, label]) => <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}<ArrowUpRight /></a>)}</nav>}
    <main id="main-content">
      <section className="hero" id="welcome" aria-labelledby="hero-title">
        <div className="hero__bg" aria-hidden="true"><div className="wall__plane">{fallbackPosters.map((poster, column) => <div className="wall__col" key={poster}><div className="wall__track">{Array.from({ length: 12 }, (_, row) => <div className="poster" key={row}><img src={posters.length ? posters[(column + row % 6) % posters.length].image : `/homepage/img/${fallbackPosters[(column + row % 6) % fallbackPosters.length]}.webp`} alt="" /></div>)}</div></div>)}</div></div>
        <div className="hero__aurora" aria-hidden="true"><span /><span /><span /></div>
        <div className="wrap hero__inner"><div className="hero__content">
          <h1 className="hero__title" id="hero-title"><span className="line"><span>The best events in {countryName}</span></span><span className="line"><span><em>all in one place.</em></span></span></h1>
          <p className="hero__sub">Concerts, raves, brunches, matches and gallery nights. Find what's on, book in seconds and see who's going.</p>
          <a className="btn btn--primary btn--lg home-discover-cta" href="#discover">Discover events <ArrowRight className="arrow" /></a>
        </div></div>
      </section>
      <section className="section today" id="today" aria-labelledby="today-title">
        <div className="wrap"><div className="section-head"><div className="section-head__text"><span className="eyebrow">Next 24 hours</span><h2 className="h2" id="today-title">Your day with <em>CityMoshi</em></h2></div>
          {(railEdges.canGoBack || railEdges.canGoForward) && <div className="section-head__aside"><button className="icon-btn" aria-label="Previous events" disabled={!railEdges.canGoBack} onClick={() => scrollRail(-1)}><ChevronLeft /></button><button className="icon-btn" aria-label="Next events" disabled={!railEdges.canGoForward} onClick={() => scrollRail(1)}><ChevronRight /></button></div>}
        </div></div>
        <div className="rail" ref={railRef} tabIndex={0} aria-label="Featured events" aria-busy={loading}>{events.map(event => <HomeEventCard key={event.id} event={event} rail />)}</div>
        {loading && <p className="wrap home-status" role="status">Loading events…</p>}
        {failed && <p className="wrap home-status" role="status">We couldn't load events right now. Please try again later.</p>}
        {!loading && !failed && events.length === 0 && <p className="wrap home-status">No events start in {countryName} in the next 24 hours. Try another country or check back soon.</p>}
      </section>
      <section className="section discover" id="discover" aria-labelledby="discover-title"><div className="wrap">
        <span id="events" className="home-anchor" />
        <div className="section-head home-discover-heading"><div className="section-head__text"><span className="eyebrow">Discover</span><h2 className="h2" id="discover-title">Find your <em>next event</em></h2></div><p className="lede">Find what's happening around you. Explore an event for the details, then book it on CityMoshi.</p></div>
        <div className="grid" aria-busy={discovery.loading}>{discovery.events.map(event => <HomeEventCard key={event.id} event={event} />)}</div>
        {discovery.loading && <p className="home-status" role="status">Loading events…</p>}
        {!discovery.loading && discovery.events.length === 0 && <div className="grid-empty"><h3>{discovery.failed ? "Events unavailable" : "Nothing here… yet"}</h3><p>{discovery.failed ? "Please check back in a moment." : `No upcoming events in ${countryName} right now. Try another country or check back soon.`}</p></div>}
      </div></section>
      <HomeMarketing />
    </main>
    <HomeFooter />
  </div>;
}
